import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import { IconButton } from 'atoms/buttons/IconButton';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Dialog } from 'components/dialog';
import { CloseButton } from 'components/dialog/CloseButton';

import { DurationsIndicator } from './DurationsIndicator';

const Root = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;
const Content = styled(Horizontal)`
  isolation: isolate;
  height: 100%;
`;
const Header = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  position: absolute;
  top: 0;
  z-index: 1;
`;
const TitleAndCloseButton = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 var(--double-unit);
`;
const StoryTitle = styled(Vertical)`
  flex: 1;
  padding-left: 40px;
`;
const BackButtonWrapper = styled.div`
  position: absolute;
  left: var(--double-unit);
`;
const NextButtonWrapper = styled.div`
  position: absolute;
  right: var(--double-unit);
`;

export type StorySlide = {
  onEnd?: () => void;
  withBackChevron?: boolean;
  withNextChevron?: boolean;
  autoPlayNext?: boolean;
  duration: number;
  bodyElement:
    | ReactNode
    | ((props: { onNext: () => void; onBack: () => void }) => ReactNode);
  titleElement?: ReactNode;
};
type Props = {
  onClose: () => void;
  stories: StorySlide[];
  pause?: boolean;
  handleOnPause: (e?: React.MouseEvent | React.TouchEvent) => void;
  handleOnUnpause: (e?: React.MouseEvent | React.TouchEvent) => void;
  goBackToBeginning?: boolean;
};
export const Story = ({
  onClose: onCloseProp,
  stories,
  pause = false,
  handleOnPause,
  handleOnUnpause,
  goBackToBeginning = true,
}: Props) => {
  const [currentlyPlayingStoryIndex, setCurrentlyPlayingStoryIndex] =
    useState(0);

  const currentStory = stories.at(currentlyPlayingStoryIndex);
  const currentStoryOptions = {
    autoPlayNext: currentStory?.autoPlayNext ?? true,
    withBackChevron: currentStory?.withBackChevron ?? true,
    withNextChevron: currentStory?.withNextChevron ?? true,
  };

  const onBack = () => {
    setCurrentlyPlayingStoryIndex(index => Math.max(0, index - 1));
  };
  const onNext = useCallback(() => {
    stories[currentlyPlayingStoryIndex]?.onEnd?.();
    const nextStory = stories.at(currentlyPlayingStoryIndex + 1);
    if (!nextStory) {
      if (goBackToBeginning) {
        setTimeout(() => {
          setCurrentlyPlayingStoryIndex(0);
        }, 1000);
      } else {
        onCloseProp();
      }
      return;
    }
    setCurrentlyPlayingStoryIndex(index => (index + 1) % stories.length);
  }, [currentlyPlayingStoryIndex, goBackToBeginning, onCloseProp, stories]);

  const onClose = () => {
    stories[currentlyPlayingStoryIndex]?.onEnd?.();
    onCloseProp();
  };

  const onStoryEnd = useCallback(() => {
    if (!currentStoryOptions.autoPlayNext) {
      handleOnPause();
      return;
    }
    onNext();
  }, [currentStoryOptions.autoPlayNext, handleOnPause, onNext]);

  if (!stories.length) {
    return null;
  }

  const bodyElement =
    typeof currentStory?.bodyElement === 'function'
      ? currentStory?.bodyElement({ onNext, onBack })
      : currentStory?.bodyElement;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth hideHeader>
      <Root
        onMouseDown={handleOnPause}
        onTouchStart={handleOnPause}
        onTouchEnd={handleOnUnpause}
        onMouseUp={handleOnUnpause}
      >
        <Header>
          <DurationsIndicator
            steps={stories.map(({ duration }) => duration)}
            playing
            currentlyPlayingStoryIndex={currentlyPlayingStoryIndex}
            onStoryEnd={onStoryEnd}
            pause={pause}
          />
          <TitleAndCloseButton>
            <StoryTitle center>
              {stories.at(currentlyPlayingStoryIndex)?.titleElement}
            </StoryTitle>
            <CloseButton transparent onClose={onClose} />
          </TitleAndCloseButton>
        </Header>
        <Content>
          {bodyElement}
          {!!currentStoryOptions.withBackChevron &&
            currentlyPlayingStoryIndex > 0 && (
              <BackButtonWrapper>
                <IconButton
                  size="medium"
                  icon={faChevronLeft}
                  onClick={onBack}
                  disableDebounce
                  color="transparent"
                />
              </BackButtonWrapper>
            )}
          {!!currentStoryOptions.withNextChevron &&
            currentlyPlayingStoryIndex < stories.length - 1 && (
              <NextButtonWrapper>
                <IconButton
                  size="medium"
                  icon={faChevronRight}
                  onClick={onNext}
                  color="transparent"
                />
              </NextButtonWrapper>
            )}
        </Content>
      </Root>
    </Dialog>
  );
};
